import { ContentCopy, Edit, PowerSettingsNew } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DateLabel, EnabledStatusLabel, DescriptionRow } from 'components';
import { useProviderTypes } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { DynamicAdapterConfig } from 'types';
import { formatUtils } from 'utils';

type Props = {
  config: DynamicAdapterConfig;
  onEdit: () => void;
  onToggleActive: () => void;
  onClone: () => void;
};

const KEYS = {
  id: 'pages.dynamic_adapter_configs.details.id',
  name: 'pages.dynamic_adapter_configs.details.name',
  provider_type: 'pages.dynamic_adapter_configs.details.provider_type',
  version: 'pages.dynamic_adapter_configs.details.version',
  status: 'pages.dynamic_adapter_configs.details.status',
  config_type: 'pages.dynamic_adapter_configs.details.config_type',
  payin_endpoints: 'pages.dynamic_adapter_configs.details.payin_endpoints',
  payout_endpoints: 'pages.dynamic_adapter_configs.details.payout_endpoints',
  created_at: 'pages.dynamic_adapter_configs.details.created_at',
  updated_at: 'pages.dynamic_adapter_configs.details.updated_at',
  created_by: 'pages.dynamic_adapter_configs.details.created_by',
} as const;

export const DynamicAdapterConfigDetails: React.FC<Props> = ({
  config,
  onEdit,
  onToggleActive,
  onClone,
}) => {
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { providerTypeOptions } = useProviderTypes();

  const providerTypeLabel = useMemo(() => {
    const option = providerTypeOptions.find(
      (opt: { value: string; label: string }) =>
        opt.value === config.providerType,
    );
    return option?.label || config.providerType;
  }, [providerTypeOptions, config.providerType]);

  const getConfigType = () => {
    if (config.payinConfig && config.payoutConfig) {
      return `${tCommon('common.payin')} + ${tCommon('common.payout')}`;
    }
    if (config.payinConfig) {
      return tCommon('common.payin');
    }
    if (config.payoutConfig) {
      return tCommon('common.payout');
    }
    return '-';
  };

  return (
    <div className="tw-p-4">
      <Stack spacing={3}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={onEdit}
          >
            {tCommon('components.crud_table.actions.details' as any)}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopy />}
            onClick={onClone}
          >
            {tCommon('components.crud_table.actions.clone' as any)}
          </Button>
          <Button
            variant={config.isActive ? 'outlined' : 'contained'}
            size="small"
            color={config.isActive ? 'warning' : 'success'}
            startIcon={<PowerSettingsNew />}
            onClick={onToggleActive}
          >
            {formatUtils.formatEnabled(!config.isActive)}
          </Button>
        </Stack>

        <DescriptionRow title={t(KEYS.id)} value={config.id} canCopy />

        <DescriptionRow title={t(KEYS.name)} value={config.name} />

        <DescriptionRow
          title={t(KEYS.provider_type)}
          value={providerTypeLabel}
        />

        <DescriptionRow title={t(KEYS.version)} value={config.version} />

        <DescriptionRow
          title={t(KEYS.status)}
          value={
            <EnabledStatusLabel
              status={config.isActive ? 'active' : 'inactive'}
            />
          }
        />

        <DescriptionRow title={t(KEYS.config_type)} value={getConfigType()} />

        {config.payinConfig && (
          <div>
            <Typography variant="caption" color="text.secondary">
              {t(KEYS.payin_endpoints)}
            </Typography>
            <Typography variant="body2">
              {'Create: '}
              {config.payinConfig.endpoints.create.method}{' '}
              {config.payinConfig.endpoints.create.path}
            </Typography>
            <Typography variant="body2">
              {'Get: '}
              {config.payinConfig.endpoints.get.method}{' '}
              {config.payinConfig.endpoints.get.path}
            </Typography>
            {config.payinConfig.endpoints.cancel && (
              <Typography variant="body2">
                {'Cancel: '}
                {config.payinConfig.endpoints.cancel.method}{' '}
                {config.payinConfig.endpoints.cancel.path}
              </Typography>
            )}
          </div>
        )}

        {config.payoutConfig && (
          <div>
            <Typography variant="caption" color="text.secondary">
              {t(KEYS.payout_endpoints)}
            </Typography>
            <Typography variant="body2">
              {'Create: '}
              {config.payoutConfig.endpoints.create.method}{' '}
              {config.payoutConfig.endpoints.create.path}
            </Typography>
            <Typography variant="body2">
              {'Get: '}
              {config.payoutConfig.endpoints.get.method}{' '}
              {config.payoutConfig.endpoints.get.path}
            </Typography>
          </div>
        )}

        <DescriptionRow
          title={t(KEYS.created_at)}
          value={
            <DateLabel>{formatUtils.formatDate(config.createdAt)}</DateLabel>
          }
        />

        <DescriptionRow
          title={t(KEYS.updated_at)}
          value={
            <DateLabel>{formatUtils.formatDate(config.updatedAt)}</DateLabel>
          }
        />

        {config.createdBy && (
          <DescriptionRow title={t(KEYS.created_by)} value={config.createdBy} />
        )}
      </Stack>
    </div>
  );
};
